import { FC, ReactNode, useEffect, useState } from "react";
import { GlobalToastContainer } from "@/components/toast/global-container";
import { AtlasRouter } from "@/app/router/router";
import { AtlasStyleWrapper } from "@/domains/styling/wrapper";
import { AtlasAuthProvider } from "@/contexts/atlas-auth";
import { AtlasAssetTokenProvider } from "@/contexts/atlas-asset-token";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Theme, ThemeProps } from "@radix-ui/themes";
import { AtlasGlobalProvider } from "@/domains/styling/global-provider";
import { isIOSDevice } from "@/utils/device-detection";
import { ThemePreferenceProvider, useThemePreference } from "@/contexts/theme-preference";
import { ConvexProvider, ConvexReactClient } from "convex/react";

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL as string);

const atlasTheme: ThemeProps = {
  accentColor: "violet",
  grayColor: "gray",
  scaling: "90%",
  radius: "small",
};

export const AtlasCore: FC = () => {
  const client = new QueryClient();
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check for iOS on mount only - iOS detection doesn't change during session
    setIsIOS(isIOSDevice());
  }, []);

  const theme = { ...atlasTheme, scaling: isIOS ? "110%" : atlasTheme.scaling };

  return (
    <ConvexProvider client={convex}>
      <AtlasAuthProvider>
        <AtlasAssetTokenProvider>
          <QueryClientProvider client={client}>
            <ThemePreferenceProvider>
              <AtlasThemeAppearance themeProps={theme}>
                <AtlasStyleWrapper>
                  <AtlasGlobalProvider>
                    <AtlasRouter />
                    <GlobalToastContainer />
                  </AtlasGlobalProvider>
                </AtlasStyleWrapper>
              </AtlasThemeAppearance>
            </ThemePreferenceProvider>
          </QueryClientProvider>
        </AtlasAssetTokenProvider>
      </AtlasAuthProvider>
    </ConvexProvider>
  );
};

type AtlasThemeAppearanceProps = {
  themeProps: ThemeProps;
  children: ReactNode;
};

const AtlasThemeAppearance = ({ themeProps, children }: AtlasThemeAppearanceProps) => {
  const { resolvedTheme } = useThemePreference();
  const themeAppearance: ThemeProps["appearance"] =
    resolvedTheme === "dark" ? "light" : resolvedTheme;

  return (
    <Theme {...themeProps} appearance={themeAppearance}>
      {children}
    </Theme>
  );
};
